import { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Select, SelectContent, SelectItem, SelectTrigger,
} from "@/components/ui/select";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { Menu, ArrowLeftRight, MapPin, Truck, Container, Plus, DoorOpen, ClipboardCheck } from "lucide-react";
import { UserProfilePopover } from "@/components/layout/UserProfilePopover";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import auroraLogo from "@/assets/aurora-brand-logo.svg";

import { YardSiteView } from "@/components/yard/YardSiteView";
import { YardAuditView } from "@/components/yard/YardAuditView";
import { CheckInForm, type CheckInFormHandle } from "@/components/yard/CheckInForm";
import { useYardAction } from "@/hooks/useYardAction";
import { X } from "lucide-react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { useTenant } from "@/hooks/useTenant";
import { EnvironmentSwitcher, useEnvironmentBorderClass } from "@/components/layout/EnvironmentSwitcher";
import { useUserRole } from "@/hooks/useUserRole";
import { canUseYard } from "@/lib/role-access";
import { Lock } from "lucide-react";

interface YardLocation {
  id: string;
  short_code: string;
  city: string | null;
  street_address: string | null;
  province_state: string | null;
}

type YardView = "gate_check" | "yard_audit";

// Shown in the yard picker even though it isn't tracked in YMS.
const UNTRACKED_YARD_SHORT_CODES = ["HTSH-DAL-01"] as const;

export default function Yard() {
  const [selectedLocationId, setSelectedLocationId] = useState<string>("");
  const [showAddFoundTrailer, setShowAddFoundTrailer] = useState(false);
  const [foundTrailerPhoto, setFoundTrailerPhoto] = useState<File | null>(null);
  const [foundTrailerPhotoRight, setFoundTrailerPhotoRight] = useState<File | null>(null);
  const [foundTrailerSealPhoto, setFoundTrailerSealPhoto] = useState<File | null>(null);
  const [foundTrailerLicensePlatePhoto, setFoundTrailerLicensePlatePhoto] = useState<File | null>(null);
  const [foundTrailerBolPhoto, setFoundTrailerBolPhoto] = useState<File | null>(null);
  const checkInSubmitRef = useRef<CheckInFormHandle | null>(null);
  const queryClient = useQueryClient();
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeView, setActiveView] = useState<YardView>("gate_check");
  
  const envBorderClass = useEnvironmentBorderClass();
  const { activeTenant } = useTenant();
  const { isReadOnly: officeReadOnly, roles } = useUserRole();
  const isReadOnly = officeReadOnly && !canUseYard(roles);

  const { data: locations = [] } = useQuery({
    queryKey: ["locations", "yard"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("locations")
        .select("id, short_code, city, street_address, province_state")
        .eq("archived", false)
        .or(`track_in_yms.eq.true,short_code.in.(${UNTRACKED_YARD_SHORT_CODES.join(",")})`)
        .order("short_code");
      if (error) throw error;
      return data as YardLocation[];
    },
  });

  const locationIds = locations.map((l) => l.id);

  const { tenantId } = useTenant();

  const { data: trailerCounts = {} } = useQuery({
    queryKey: ["yard-trailer-counts", locationIds, tenantId],
    queryFn: async () => {
      if (!locationIds.length) return {};
      let query = supabase
        .from("trailers")
        .select("current_location_id")
        .in("current_location_id", locationIds)
        .eq("current_location_status", "at_site");
      if (tenantId) query = query.eq("tenant_id", tenantId);
      const { data, error } = await query;
      if (error) throw error;
      const counts: Record<string, number> = {};
      data.forEach((t) => {
        if (t.current_location_id) {
          counts[t.current_location_id] = (counts[t.current_location_id] || 0) + 1;
        }
      });
      return counts;
    },
    enabled: locationIds.length > 0,
  });

  const { data: tractorCounts = {} } = useQuery({
    queryKey: ["yard-tractor-counts", locationIds],
    queryFn: async () => {
      if (!locationIds.length) return {};
      const { data, error } = await supabase
        .from("tractors")
        .select("home_base_id")
        .in("home_base_id", locationIds);
      if (error) throw error;
      const counts: Record<string, number> = {};
      data.forEach((t) => {
        if (t.home_base_id) {
          counts[t.home_base_id] = (counts[t.home_base_id] || 0) + 1;
        }
      });
      return counts;
    },
    enabled: locationIds.length > 0,
  });

  const selectedLocation = locations.find((l) => l.id === selectedLocationId);

  const foundTrailerAction = useYardAction({
    locationId: selectedLocationId,
    locationCity: selectedLocation?.city || "",
    direction: "in",
    pendingPhoto: foundTrailerPhoto,
    pendingPhotoRight: foundTrailerPhotoRight,
    pendingSealPhoto: foundTrailerSealPhoto,
    pendingLicensePlatePhoto: foundTrailerLicensePlatePhoto,
    pendingBolPhoto: foundTrailerBolPhoto,
    onPhotoUploaded: () => setFoundTrailerPhoto(null),
    onPhotoRightUploaded: () => setFoundTrailerPhotoRight(null),
    onSealPhotoUploaded: () => setFoundTrailerSealPhoto(null),
    onLicensePlatePhotoUploaded: () => setFoundTrailerLicensePlatePhoto(null),
    onBolPhotoUploaded: () => setFoundTrailerBolPhoto(null),
  });


  const handleSwitchView = (view: YardView) => {
    setActiveView(view);
    setSelectedLocationId("");
    setMenuOpen(false);
  };

  const formatAddress = (loc: YardLocation) => {
    return [loc.street_address, loc.city, loc.province_state]
      .filter(Boolean)
      .join(", ");
  };

  const HeaderIcon = activeView === "gate_check" ? DoorOpen : ClipboardCheck;
  const headerTitle = activeView === "gate_check" ? "Gate check" : "Yard audit";

  return (
    <div className="min-h-screen bg-background flex flex-col">
       <header className={`text-foreground p-4 flex items-center justify-between bg-card ${envBorderClass}`}>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="text-foreground hover:text-foreground/80"
            onClick={() => setMenuOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-bold flex items-center gap-1.5"><HeaderIcon className="h-5 w-5" />{headerTitle}</h1>
          <EnvironmentSwitcher />
        </div>
        <div className="text-foreground">
          <UserProfilePopover compact />
        </div>
      </header>




      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side="left" className="w-72 p-0 flex flex-col">
          <SheetHeader className="p-4 border-b">
            <SheetTitle className="text-left">
              <div className="flex items-center gap-3">
                <img src={auroraLogo} alt="Aurora logo" className="h-8 w-8 object-contain" />
                <div>
                  <span className="text-lg font-bold block">AYMS</span>
                  <p className="text-xs text-muted-foreground font-normal">Aurora Yard Management</p>
                </div>
              </div>
            </SheetTitle>
          </SheetHeader>
          <nav className="p-2 space-y-1 flex-1">
            <button
              onClick={() => handleSwitchView("gate_check")}
              className={`w-full text-left rounded-md px-3 py-2.5 text-base font-medium transition-colors flex items-center gap-2 ${
                activeView === "gate_check" ? "bg-accent text-accent-foreground" : "hover:bg-accent text-muted-foreground"
              }`}
            >
              <DoorOpen className="h-5 w-5" />
              Gate check
            </button>
            <button
              onClick={() => handleSwitchView("yard_audit")}
              className={`w-full text-left rounded-md px-3 py-2.5 text-base font-medium transition-colors flex items-center gap-2 ${
                activeView === "yard_audit" ? "bg-accent text-accent-foreground" : "hover:bg-accent text-muted-foreground"
              }`}
            >
              <ClipboardCheck className="h-5 w-5" />
              Yard audit
            </button>
          </nav>
          <div className="px-3 py-3 border-t">
            <EnvironmentSwitcher variant="sidebar" />
          </div>
        </SheetContent>
      </Sheet>

      {selectedLocation ? (
        <>
          <div className="p-4 pb-0">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-semibold">{selectedLocation.short_code}</h1>
                <Select value={selectedLocationId} onValueChange={setSelectedLocationId}>
                  <SelectTrigger className="w-9 h-9 p-0 border rounded-md [&>svg:last-child]:hidden [&>span]:hidden flex items-center justify-center">
                    <ArrowLeftRight className="h-4 w-4" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map((loc) => (
                      <SelectItem key={loc.id} value={loc.id}>
                        {loc.short_code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {activeView === "yard_audit" && !isReadOnly && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setShowAddFoundTrailer(true)}
                      >
                        <Plus className="h-4 w-4 mr-1.5" />
                        Add found trailer
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Check in a trailer found during audit</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </div>

          {isReadOnly ? (
            <div className="flex-1 p-6 flex items-start justify-center">
              <div className="max-w-md w-full mt-12 rounded-lg border border-dashed p-6 text-center bg-card">
                <Lock className="h-6 w-6 mx-auto mb-3 text-muted-foreground" />
                <h2 className="text-lg font-semibold mb-1">Read-only access</h2>
                <p className="text-sm text-muted-foreground">
                  Yard check-ins, check-outs, and audits are disabled for analyst
                  accounts. Sign in with an operator account to perform yard actions.
                </p>
              </div>
            </div>
          ) : activeView === "gate_check" ? (
            <YardSiteView
              locationId={selectedLocationId}
              locationCity={selectedLocation.city || ""}
              locationName={selectedLocation.short_code}
            />
          ) : (
            <YardAuditView
              locationId={selectedLocationId}
              locationName={selectedLocation.short_code}
            />
          )}
        </>
      ) : (
        <div className="flex-1 p-4 space-y-4">
          <h1 className="text-2xl font-semibold">Choose a site</h1>
          <div className="space-y-2">
            {locations.map((loc) => {
              const address = formatAddress(loc);
              const trailers = trailerCounts[loc.id] || 0;
              const tractors = tractorCounts[loc.id] || 0;
              return (
                <button
                  key={loc.id}
                  onClick={() => setSelectedLocationId(loc.id)}
                  className="w-full text-left rounded-lg border bg-card p-4 hover:border-primary/50 hover:shadow-sm transition-all active:scale-[0.98]"
                >
                  <div className="flex items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-lg">{loc.short_code}</p>
                      {address && (
                        <p className="text-sm text-muted-foreground mt-0.5 truncate">{address}</p>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1.5">
                        <Container className="h-3.5 w-3.5" />
                        {trailers}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Truck className="h-3.5 w-3.5" />
                        {tractors}
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Add found trailer overlay (Yard Audit) */}
      {showAddFoundTrailer && selectedLocation && (
        <div className="fixed inset-0 z-50 bg-background overflow-auto">
          <div className="p-8 pb-24">
            <div className="flex items-center gap-3 mb-6">
              <button
                onClick={() => setShowAddFoundTrailer(false)}
                className="p-1.5 rounded-md hover:bg-muted transition-colors active:scale-95"
              >
                <X className="h-6 w-6" />
              </button>
              <h2 className="text-2xl font-semibold">Add found trailer</h2>
            </div>

            <CheckInForm
              hideTitle
              hideTruckId
              isFoundTrailer
              locationId={selectedLocationId}
              locationCity={selectedLocation.city || ""}
              locationName={selectedLocation.short_code}
              isProcessing={foundTrailerAction.isProcessing}
              pendingPhoto={foundTrailerPhoto}
              onPhotoChange={setFoundTrailerPhoto}
              pendingPhotoRight={foundTrailerPhotoRight}
              onPhotoRightChange={setFoundTrailerPhotoRight}
              pendingSealPhoto={foundTrailerSealPhoto}
              onSealPhotoChange={setFoundTrailerSealPhoto}
              pendingLicensePlatePhoto={foundTrailerLicensePlatePhoto}
              onLicensePlatePhotoChange={setFoundTrailerLicensePlatePhoto}
              pendingBolPhoto={foundTrailerBolPhoto}
              onBolPhotoChange={setFoundTrailerBolPhoto}
              processManualAction={foundTrailerAction.processManualAction}
              createAndProcess={foundTrailerAction.createAndProcess}
              onSuccess={() => {
                setShowAddFoundTrailer(false);
                setFoundTrailerPhoto(null);
                setFoundTrailerPhotoRight(null);
                setFoundTrailerSealPhoto(null);
                setFoundTrailerLicensePlatePhoto(null);
                setFoundTrailerBolPhoto(null);
                queryClient.invalidateQueries({ queryKey: ["yard-audit-trailers", selectedLocationId] });
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
